'use client';

import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  getFirstPermittedRoute,
  resolveFallbackRoute,
} from '@/shared/constants/route-permissions';

import { permissionsQueryOptions } from './auth-queries';
import {
  mcpConnectionsAccessQueryOptions,
  useMcpConnectionsAccess,
} from './mcp-connections-access';
import { useAuth } from './useAuth';
import { usePermissions } from './usePermissions';

export interface FirstPermittedRouteState {
  /**
   * The first mapped route the active role holds; else `UNION_GATED_ROUTE`
   * (`/mcp-connections`) when the gateway allows MCP; else `undefined`.
   * Always `undefined` while `isLoading`.
   */
  route: string | undefined;
  /**
   * True until the permissions are known and — ONLY when the active role maps
   * no route — until the MCP answer is final too (a stale denial being
   * re-read, or a read paused offline, is not). When the answer would be a
   * DENIAL (no mapped route, no MCP), also while the permissions read itself
   * is unsettled (`usePermissions().isFetching`: a refetch over stale
   * permissions, or a read paused offline). A role that holds a screen never
   * waits.
   */
  isLoading: boolean;
  /**
   * For imperative callers (Access Denied after a role switch): re-reads the
   * permissions and, when they map no route, the MCP answer — both are
   * invalidated by the switch. A failed permissions read rejects (the caller
   * explains it); a failed MCP read is a denial, as in the hook.
   */
  resolve: () => Promise<string | undefined>;
}

/**
 * Where to send a user who has no specific destination: after the login (`/`),
 * from a denied route (`useRouteGuard`), and from Access Denied after a role
 * switch (AB#28616).
 *
 * `/mcp-connections` follows the union of the user's roles, which only the
 * gateway knows, so the answer is asynchronous; this hook keeps that wait in
 * one place. Deciding before it arrives would flash Access Denied at a user
 * whose only access is MCP.
 */
export function useFirstPermittedRoute(): FirstPermittedRouteState {
  const queryClient = useQueryClient();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const {
    permissions,
    isLoading: permissionsLoading,
    isFetching: permissionsFetching,
  } = usePermissions();
  const { allowed: mcpAllowed, isLoading: mcpReadLoading } =
    useMcpConnectionsAccess();

  const mappedRoute = getFirstPermittedRoute(permissions);
  // The MCP answer is unsettled while `useMcpConnectionsAccess` says it is
  // not final (first read, a stale denial being re-read, a read paused
  // offline) — and while the read is disabled (no session yet), which is an
  // unanswered read, not a "no". Without a resolved session there is no
  // answer to wait for; AuthProvider sends that visitor to the login.
  const mcpPending = isAuthLoading || !isAuthenticated || mcpReadLoading;
  const fallbackRoute = resolveFallbackRoute(permissions, mcpAllowed);
  // A DENIAL (no mapped route, no MCP) is final only once the permissions read
  // is too: a refetch over stale permissions (`[]` cached) or a read paused
  // offline may still grant a route, and redirecting now would strand the
  // user on Access Denied. A mapped route or MCP access never waits on it.
  const denialUnsettled =
    fallbackRoute === undefined && permissionsFetching === true;
  const isLoading =
    permissionsLoading ||
    (mappedRoute === undefined && (mcpPending || denialUnsettled));

  const resolve = useCallback(async (): Promise<string | undefined> => {
    const { permissions: fresh } = await queryClient.fetchQuery(
      permissionsQueryOptions(true)
    );
    const mapped = getFirstPermittedRoute(fresh);
    if (mapped !== undefined) return mapped;

    const allowed = await queryClient
      .fetchQuery(mcpConnectionsAccessQueryOptions(true))
      .catch(() => false);
    return resolveFallbackRoute(fresh, allowed);
  }, [queryClient]);

  return {
    route: isLoading ? undefined : fallbackRoute,
    isLoading,
    resolve,
  };
}
